import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  SearchFacetMenuSelect,
  SearchFacetRefinementCheckboxes,
  SearchFacets,
  SearchResults,
  SearchSortBy,
  SearchStats,
  SearchkitWrapper,
  SearchPagination,
} from '@uncinc/react-searchkit';

import SearchBarCustom from './SearchBar';
import TeaserSmall from '../../components/Teaser/Small';

import {
  searchAttributes,
  resultAttributes,
  facetAttributes,
  sorting,
  requestOptions,
} from './config';

import SearchTypeFacet from './SearchTypeFacet';

import { API_URL, SEARCHKIT_HOST_URL, APP_OPTIONS } from '../../config/constants';

import './style.scss';

// Mapping for page type labels when no specific article/document type is available
const PAGE_TYPE_LABELS = {
  basic_page: 'Informatiepagina',
  landing_page: 'Hoofdpagina',
  overview_page: 'Overzichtspagina',
  document: 'Document',
};

const Item = ({ hit }) => {
  let publicationDate = null;

  // The "combined_created" field is a union between "created" and "publication_date".
  if (hit.combined_created?.[0]) {
    if (hit.combined_created?.[1]) {
      // Publication date has priority.
      publicationDate = hit.combined_created[1] * 1000;
    } else {
      // Created date needs to be converted when set.
      publicationDate = hit.combined_created[0] * 1000;
    }
  }

  const summary = hit.body_summarized?.[0] || hit.body?.[0] || hit.description?.[0];
  const title = hit.page_title;
  const typeLabel = hit.article_type_name?.[0] || hit.document_type_name?.[0] || PAGE_TYPE_LABELS[hit.type?.[0]] || hit.type?.[0];
  const themes = hit.themes;
  const type = hit.type?.[0];
  const url = hit.url?.[0];
  let imageUrl = null;

  if (hit.poster_image) {
    imageUrl = `${API_URL}${hit.poster_image?.[0]}`;
  } else if (hit.image_style_uri) {
    imageUrl = hit.image_style_uri?.[0];
  }

  return (
    <TeaserSmall
      title={title}
      image={imageUrl}
      themes={themes}
      date={publicationDate}
      url={url}
      summary={summary}
      type={type}
      typeLabel={typeLabel}
      isSearchResult
      className={`image-right ${type}`}
    />
  );
};

const App = () => {
  const { t } = useTranslation();

  return (
    <Container className="Search">
      <SearchkitWrapper
        connection={{
          host: SEARCHKIT_HOST_URL,
        }}
        indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
        hitsPerPage={8}
        initialUiState={{
          [APP_OPTIONS.parameters.searchkitIndexAllContent]: {
            sortBy: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_created_desc`,
          },
        }}
        routing
        searchSettings={{
          search_attributes: searchAttributes,
          result_attributes: resultAttributes,
          facet_attributes: facetAttributes,
          sorting: sorting,
        }}
        requestOptions={requestOptions}
      >
        <div className="row">
          <div className="col-12">
            <h1>Zoeken</h1>
            <div className="row">
              <div className="col-12 col-lg-8">
                <SearchBarCustom />
              </div>
            </div>
          </div>
        </div>
        <div className="row SearchContent">
          <div className="col-12 col-lg-4 SearchContent__filters">
            <SearchFacets>
              <div className="SearchContent__combined_type_filter">
                <SearchFacetRefinementCheckboxes
                  title={t('components.search.facets.article_type.label')}
                  className="Checkboxes"
                  itemClassName="Checkboxes__item"
                  attribute="combined_type_filter"
                />
              </div>
              <SearchTypeFacet />
              <SearchFacetRefinementCheckboxes
                title={t('components.search.facets.document_type.label')}
                className="Checkboxes"
                itemClassName="Checkboxes__item"
                attribute="document_type_name"
              />
              <SearchFacetMenuSelect
                placeholder={t('components.search.facets.theme.placeholder')}
                title={t('components.search.facets.theme.label')}
                attribute="entity_theme_names"
                sortBy={['name:asc']}
              />
            </SearchFacets>
          </div>
          <div className="col-12 col-lg-8 SearchContent__content">
            <div className="Searchkit__Header">
              <SearchStats />
              <SearchSortBy
                className="Searchkit__sortByMenu"
                isSearchable={false}
                placeholder={t('components.search.sort.label')}
                items={[
                  { label: t('components.search.sort.relevance'), value: APP_OPTIONS.parameters.searchkitIndexAllContent },
                  { label: t('components.search.sort.date_asc'), value: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_created_asc` },
                  { label: t('components.search.sort.date_desc'), value: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_created_desc` },
                  { label: t('components.search.sort.page_title_asc'), value: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_page_title_asc` },
                  { label: t('components.search.sort.page_title_desc'), value: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_page_title_desc` },
                ]}
              />
            </div>
            <SearchResults renderItem={(hit) => <Item hit={hit} />} />
            <SearchPagination
              previousLabel={<div className="Pagination__previous"><i className="icon-before icon-arrow-right icon-variant-4" /><span>{t('searchkit.buttons.previous')}</span></div>}
              nextLabel={<div className="Pagination__next"><span>{t('searchkit.buttons.next')}</span><i className="icon-after icon-arrow-right icon-variant-4" /></div>}
            />
          </div>
        </div>
      </SearchkitWrapper>
    </Container>
  );
};

export default App;
